import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Row, Col, Alert, Checkbox, message, notification, Modal } from 'antd';
import { usePaymentService } from '../../../services/payments/PaymentServiceProvider';
import { CreateDirectDebitRequest } from '../../../requests/payments/CreateDirectDebitRequest';
import { useMember } from '../../../common/member/MemberContext';
import { PaymentPermissions, AdminPermissions } from '../../../model/auth/Permissions';
// Placeholder / blank detection is shared with the profile-completion banner so
// the two agree on what "no mandate on file" means.
import { isMandateFieldEmpty as isFieldEmpty, isMandateSelfEditable } from './v2/completion';

interface DirectDebitInputProps {
    membershipId: string;
    backgroundColor?: string;
    borderColor?: string;
    selfServiceMode?: boolean;
    // When false, the form is read-only (no editing, no Save). Defaults to true.
    canEdit?: boolean;
    // When true, the sort code and account number are masked (e.g. front-of-house
    // can confirm the last digits without seeing or editing full bank details).
    maskDetails?: boolean;
    // When true, the member is still being created (no membershipId yet): skip the
    // fetch (nothing to load) and hide the internal Save button. If the fields are
    // editable (onboarding), the entered mandate is reported via onCreateChange and
    // submitted with the create request instead of saved here.
    isCreating?: boolean;
    // Only used while isCreating — reports the entered mandate up to the parent so
    // it can be sent with the member-create request.
    onCreateChange?: (values: FormValues) => void;
    // Whether the four mandate fields are required. Defaults to true (a saved
    // mandate needs all four). The admin/front-of-house create flow sets this
    // false so an intentionally-blank mandate doesn't show required markers or
    // raise validation errors; format rules still apply once a field is typed in.
    mandateRequired?: boolean;
    // Self-service onboarding only: show a note + opt-out checkbox letting the
    // member defer direct-debit setup. When ticked (`skipped`), the fields grey out
    // and the parent drops the DD requirement so they can save without it.
    allowSkip?: boolean;
    skipped?: boolean;
    onSkipChange?: (skipped: boolean) => void;
    // Client-specific direct-debit cancellation terms (from client config). When
    // set and the mandate is being set up (blank / creating, not skipped), the
    // member must tick an agreement box before the mandate can be saved. Once a
    // mandate is already on file the same text is shown read-only, without a box.
    // Empty / undefined => no notice is shown at all.
    cancellationNotice?: string;
    // Reports the agreement tick state up so a create flow can gate Save on it.
    // Only meaningful while the agreement box is shown (setting up the mandate).
    onAgreedChange?: (agreed: boolean) => void;
    // AntD derives each input's DOM id from the form name + field name. Pass a name
    // when a second copy of this panel can be mounted at the same time (e.g. the
    // profile-completion wizard opening over a profile whose Billing tab already
    // renders one) so the ids stay unique and each label binds to its own input.
    formName?: string;
    // Fired after this panel successfully saves a mandate. Lets a parent that
    // separately tracks mandate state (e.g. the profile-completion banner) re-read
    // it instead of going stale until a reload.
    onSaved?: () => void;
}

interface FormValues {
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    sortCode: string;
}

// True once the member has typed real data into ANY of the four mandate fields —
// i.e. they've started setting up a mandate (vs. an untouched empty panel).
const anyMandateFieldFilled = (values: FormValues): boolean =>
    !isFieldEmpty(values.accountHolderName) ||
    !isFieldEmpty(values.bankName) ||
    !isFieldEmpty(values.accountNumber) ||
    !isFieldEmpty(values.sortCode);

// A mandate with nothing in it — what an explicit removal posts, and what the fields
// are reset to once the server has accepted it.
const EMPTY_MANDATE: FormValues = {
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    sortCode: ''
};

// Removing a live mandate stops future collections, so make staff say so out loud
// rather than let a stray clear-and-save do it silently. Resolves true on confirm.
// Takes the hook-based modal API rather than calling `Modal.confirm` statically:
// static AntD methods render outside the tree and so never see the ConfigProvider
// that App.tsx installs, which would put this dialog off the tenant's theme.
type ModalApi = ReturnType<typeof Modal.useModal>[0];

const confirmRemoval = (
    modal: ModalApi,
    mandateSummary: string,
    // True when the form holds edits that are about to be thrown away — the standalone
    // button is clickable mid-edit, and the summary describes what's ON FILE, so without
    // this nothing on screen says the typed details are going too.
    discardsUnsavedInput: boolean
): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
        modal.confirm({
            title: 'Remove direct debit details?',
            content:
                `This clears ${mandateSummary}. No further direct debit payments can be collected until a new mandate is added.` +
                (discardsUnsavedInput ? ' The details you have typed but not saved will be discarded.' : ''),
            okText: 'Remove details',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
        });
    });

// A stored placeholder ("To be Completed", "Guest Member", all zeros) is the absence
// of a value, so it renders as an empty field rather than as text to be kept.
const blankIfPlaceholder = (value: string | undefined): string =>
    isFieldEmpty(value) ? '' : (value ?? '');

// Mask all but the last 4 digits of an account number: "12345678" -> "••••5678".
// Placeholder / empty values stay empty so the UI still reflects "not on file".
const maskAccountNumber = (value: string | undefined): string => {
    if (isFieldEmpty(value)) return '';
    const digits = (value ?? '').replace(/\D/g, '');
    if (digits.length < 4) return '••••';
    return '••••' + digits.slice(-4);
};

// Mask all but the last 2 digits of a sort code: "123456" -> "••-••-56".
// Placeholder / empty values stay empty so the UI still reflects "not on file".
const maskSortCode = (value: string | undefined): string => {
    if (isFieldEmpty(value)) return '';
    const digits = (value ?? '').replace(/\D/g, '');
    if (digits.length < 2) return '••-••-••';
    return '••-••-' + digits.slice(-2);
};

// Name the mandate the confirmation is about to take off. Removal is now one click,
// so the dialog has to let staff check they are on the member they think they are —
// generic wording reads identically on a mis-clicked profile.
const describeMandate = (values: FormValues | null): string => {
    if (!values) return "the member's bank details";
    const holder = isFieldEmpty(values.accountHolderName) ? '' : values.accountHolderName.trim();
    const account = maskAccountNumber(values.accountNumber);
    if (holder && account) return `the bank details for ${holder} (${account})`;
    if (holder) return `the bank details for ${holder}`;
    if (account) return `the bank details ending ${account}`;
    return "the member's bank details";
};

const DirectDebitInput: React.FC<DirectDebitInputProps> = ({
    membershipId,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb',
    selfServiceMode = false,
    canEdit = true,
    maskDetails = false,
    isCreating = false,
    onCreateChange,
    mandateRequired = true,
    allowSkip = false,
    skipped = false,
    onSkipChange,
    cancellationNotice,
    onAgreedChange,
    formName,
    onSaved
}) => {
    const [form] = Form.useForm<FormValues>();
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Which of the two actions is in flight — both buttons watch isSaving to disable, but
    // only the one that was clicked should show the spinner.
    const [savingAction, setSavingAction] = useState<'save' | 'remove' | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues | null>(null);
    // Which membershipId the values in state were actually loaded for. `isLoading` alone
    // can't decide this: two fetches can be in flight after a profile switch, and the
    // slower (older) one resolving last would repaint the previous member's mandate with
    // isLoading already false. Every removal affordance is gated on this matching the
    // current prop, so a stale payload can never offer to blank the wrong record.
    const [loadedFor, setLoadedFor] = useState<string | null>(null);
    // The fetch failed: we do NOT know what is on file. Distinct from "nothing on file",
    // which an empty form would otherwise imply.
    const [loadFailed, setLoadFailed] = useState(false);
    const [canEditDD, setCanEditDD] = useState(true);
    // Whether a complete mandate is already on file (all four fields present). Used
    // to decide between the agreement tick box (still setting up) and a read-only
    // notice (already agreed at setup). Stays false while creating (nothing on file).
    const [mandateComplete, setMandateComplete] = useState(false);
    // Agreement tick state. Only gates saving while the box is shown.
    const [agreed, setAgreed] = useState(false);
    // Whether the member has started entering a mandate (any field has content).
    // Used so the notice/agreement box only appears once a mandate is actually
    // being set up, not the instant an editable-but-empty panel renders (relevant
    // to admin create, where a mandate is optional).
    const [hasMandateInput, setHasMandateInput] = useState(false);
    // Hook-based confirm API so the removal dialog inherits the tenant's ConfigProvider
    // theme; `modalContextHolder` must be rendered for it to work.
    const [modal, modalContextHolder] = Modal.useModal();
    const paymentService = usePaymentService();
    const { permissions } = useMember();

    // Check if user has permission to view direct debits. While creating a member
    // there is no membershipId yet and no existing mandate is fetched, so the panel
    // is just an empty input — always allow it (including for kiosk / register-any
    // accounts that lack the read-direct-debits permission).
    const hasPermission = selfServiceMode || isCreating || permissions?.permissions.some(p =>
        p.name === PaymentPermissions.ReadDirectDebits ||
        p.name === AdminPermissions.FortiusAdmin ||
        p.name === AdminPermissions.ClubAdmin
    );

    useEffect(() => {
        // No membershipId yet while creating — nothing to fetch; show empty fields.
        if (hasPermission && !isCreating) {
            // Set when this effect is superseded (the profile changed under the panel).
            // Every setState after the await is gated on it, so a slow response for the
            // PREVIOUS member can't repaint the panel over the current one's.
            let superseded = false;
            const fetchDirectDebitInstruction = async () => {
                setIsLoading(true);
                setLoadFailed(false);
                setLoadedFor(null);
                // Drop whatever the panel is holding BEFORE the fetch. It is not remounted
                // when the profile changes under it, so without this a fetch that fails
                // (or is still in flight) leaves the previous member's mandate in
                // initialValues while membershipId is already the new member — the panel
                // then offers Remove Details, names the old member in the confirmation,
                // and blanks the NEW member's record.
                form.resetFields();
                setInitialValues(null);
                setMandateComplete(false);
                setHasMandateInput(false);
                setHasChanges(false);
                try {
                    const instruction = selfServiceMode
                        ? await paymentService.getMyDirectDebitInstruction()
                        : await paymentService.getDirectDebitInstruction(membershipId);
                    if (superseded) return;
                    if (instruction) {
                        const formValues: FormValues = {
                            // Placeholder values are not data — blank them rather than seed
                            // the fields with them. A guest/legacy row carries 'Guest Member'
                            // / 'To be Completed' / 00000000 / 000000; pre-filling those into
                            // an editable form is how a member ends up saving 'Guest Member'
                            // as their account holder name — or clicking straight past a
                            // zero-filled account number that passes the local 8-digit rule
                            // and is then rejected by the server as uncollectable.
                            // All four get the same treatment: masking blanks placeholders
                            // too, but maskDetails is false in self-service (see
                            // LayoutTwoColumn), which is exactly where this matters.
                            accountHolderName: blankIfPlaceholder(instruction.directDebitPayment.accountHolderName),
                            bankName: blankIfPlaceholder(instruction.directDebitPayment.bankName),
                            // When masking, keep only the masked form in component state so the
                            // full account number / sort code is never bound to the inputs.
                            accountNumber: maskDetails
                                ? maskAccountNumber(instruction.directDebitPayment.accountNumber)
                                : blankIfPlaceholder(instruction.directDebitPayment.accountNumber),
                            sortCode: maskDetails
                                ? maskSortCode(instruction.directDebitPayment.sortCode)
                                : blankIfPlaceholder(instruction.directDebitPayment.sortCode)
                        };
                        form.setFieldsValue(formValues);
                        setInitialValues(formValues);

                        // A mandate is complete once all four fields hold real data
                        // (no placeholder / blank / all-zeros). Drives both the
                        // agreement-box-vs-read-only-notice choice (all modes) and
                        // the self-service edit lock.
                        const hasIncompleteField = isFieldEmpty(formValues.accountHolderName) ||
                            isFieldEmpty(formValues.bankName) ||
                            isFieldEmpty(formValues.accountNumber) ||
                            isFieldEmpty(formValues.sortCode);
                        setMandateComplete(!hasIncompleteField);
                        setHasMandateInput(anyMandateFieldFilled(formValues));
                        // In self-service mode the form is editable exactly when the
                        // server would accept the save. Use the shared predicate rather
                        // than "any field is empty" — that was looser than the server
                        // gate (real bank numbers beside a blank bank name), which handed
                        // the member a Save the API could only 400.
                        if (selfServiceMode) {
                            setCanEditDD(isMandateSelfEditable(formValues));
                        }
                    } else {
                        // Set empty form for new instruction
                        const emptyValues: FormValues = {
                            accountHolderName: '',
                            bankName: '',
                            accountNumber: '',
                            sortCode: ''
                        };
                        form.setFieldsValue(emptyValues);
                        setInitialValues(emptyValues);
                        setMandateComplete(false);
                        setHasMandateInput(false);
                        setCanEditDD(true);
                    }
                    setHasChanges(false);
                    // These values belong to THIS member — record which, so the removal
                    // affordances can insist on it.
                    setLoadedFor(membershipId);
                } catch (error) {
                    if (superseded) return;
                    console.error('Error fetching direct debit instruction:', error);
                    // An empty form after a failed load would read as "no mandate on
                    // file", which is a claim we can't make — staff could tell a member
                    // their direct debit is missing, or type a new one over a live
                    // mandate. Say what actually happened and keep the form locked.
                    setLoadFailed(true);
                    notification.error({
                        message: 'Error',
                        description: 'Failed to fetch direct debit information'
                    });
                } finally {
                    if (!superseded) setIsLoading(false);
                }
            };
            fetchDirectDebitInstruction();
            return () => {
                superseded = true;
            };
        }
    }, [membershipId, hasPermission, form, selfServiceMode, maskDetails, paymentService, isCreating]);

    const handleValuesChange = (changedValues: Partial<FormValues>, allValues: FormValues) => {
        setHasMandateInput(anyMandateFieldFilled(allValues));
        if (isCreating) {
            // No membershipId to save against yet — lift the entered mandate to the
            // parent so it's submitted with the member-create request.
            onCreateChange?.(allValues);
            return;
        }
        if (!initialValues) return;

        const hasChanges = Object.keys(allValues).some(key => {
            const typedKey = key as keyof FormValues;
            return allValues[typedKey] !== initialValues[typedKey];
        });

        setHasChanges(hasChanges);
    };

    // The write half of both actions: Save Changes (after validation) and the
    // explicit Remove Details button. Sharing it keeps the two from drifting apart
    // on the request shape, the toast wording, or the post-save state.
    const persistMandate = async (values: FormValues, isRemoval: boolean) => {
        setIsSaving(true);
        setSavingAction(isRemoval ? 'remove' : 'save');
        try {
            const request: CreateDirectDebitRequest = {
                membershipId: membershipId,
                accountHolderName: values.accountHolderName,
                bankName: values.bankName,
                accountNumber: values.accountNumber,
                sortCode: values.sortCode.replace(/-/g, ''), // Remove any dashes
                // Record acceptance when the agreement box was shown (setting up a
                // mandate against a configured notice). Never on a removal — there is
                // no mandate left to have agreed to.
                cancellationNoticeAgreed: !isRemoval && requireAgreement ? agreed : undefined
            };

            if (selfServiceMode) {
                await paymentService.updateMyDirectDebitInstruction(request);
            } else {
                // The client raises the success toast itself, so the wording is passed
                // down rather than corrected afterwards — otherwise a removal shows both
                // "updated successfully" and "removed".
                await paymentService.updateDirectDebitInstruction(
                    membershipId,
                    request,
                    isRemoval ? 'Direct debit details removed' : undefined,
                );
            }

            // A removal posts blanks the member never typed, so put the cleared
            // mandate on screen too — otherwise the fields keep showing details
            // that are no longer on file.
            if (isRemoval) {
                // resetFields first: it clears any inline required-field errors left
                // over from before the removal, which setFieldsValue on its own doesn't.
                form.resetFields();
                form.setFieldsValue(values);
            }
            setInitialValues(values);
            setHasChanges(false);
            setHasMandateInput(anyMandateFieldFilled(values));

            // After a successful save, re-evaluate mandate completeness so the
            // agreement box gives way to the read-only notice once real details are
            // on file (and, in self-service mode, the form locks down).
            const hasIncompleteField = isFieldEmpty(values.accountHolderName) ||
                isFieldEmpty(values.bankName) ||
                isFieldEmpty(values.accountNumber) ||
                isFieldEmpty(values.sortCode);
            setMandateComplete(!hasIncompleteField);
            if (selfServiceMode) {
                setCanEditDD(isMandateSelfEditable(values));
            }
            onSaved?.();
        } catch (error) {
            console.error('Error saving direct debit instruction:', error);
            // Prefer the API's own message — it explains WHY (details already on
            // file and not self-editable, or a mandate that isn't collectable).
            // A generic string here turns an actionable 400 into a dead end.
            const apiMessage = error instanceof Error ? error.message?.trim() : '';
            notification.error({
                message: 'Error',
                description: apiMessage || 'Failed to save direct debit information'
            });
        } finally {
            setIsSaving(false);
            setSavingAction(null);
        }
    };

    const handleSubmit = async () => {
        const values = form.getFieldsValue();

        // Clearing every field is how staff REMOVE a mandate — the member cancelled it
        // at the bank, moved to another payment method, or the details belonged to
        // someone else. That has to stay possible, so it takes the removal path rather
        // than the required-field rules below (which would reject a deliberately blank
        // form and leave the mandate stuck on the record). Removal is staff-only: in
        // self-service the member falls through to the normal rules, which refuse a
        // blank save, and the server refuses it too.
        //
        // It is only a removal when there is something on file to remove. Without that
        // guard, `anyMandateFieldFilled` being placeholder-aware turns typing a
        // placeholder ("Guest Member", "00000000") against an empty mandate into a
        // silent no-op save; those inputs belong in the validation branch, which names
        // the offending field.
        const isRemoval = canRemoveMandate && !anyMandateFieldFilled(values);

        if (isRemoval) {
            if (!(await confirmRemoval(modal, describeMandate(initialValues), false))) return;
        } else {
            // Run the form's own required rules first. The server rejects a mandate
            // that isn't collectable (all four fields, 8/6 digits), so posting a partial
            // one — real numbers with a blank bank name, say — would 400 with nothing
            // pointing at the offending field. The checks below stay for format detail.
            try {
                await form.validateFields();
            } catch {
                return; // AntD surfaces the inline field errors.
            }

            // Block the save until the member accepts the cancellation terms (only
            // enforced while the agreement box is shown — setting up a blank mandate).
            if (requireAgreement && !agreed) {
                message.error('Please agree to the cancellation terms before saving');
                return;
            }

            // Validate sort code (6 digits)
            if (values.sortCode && !/^\d{6}$/.test(values.sortCode.replace(/-/g, ''))) {
                message.error('Sort code must be 6 digits');
                return;
            }

            // Validate account number (8 digits)
            if (values.accountNumber && !/^\d{8}$/.test(values.accountNumber)) {
                message.error('Account number must be 8 digits');
                return;
            }
        }

        await persistMandate(values, isRemoval);
    };

    // Removal used to be a hidden gesture: staff had to empty all four fields by hand
    // before the Save button would offer it. This is the same operation with a button
    // of its own — confirm, then post the blank mandate. The confirmation dialog is
    // shared with the clear-and-save path, so both spell out the consequence.
    const handleRemove = async () => {
        if (!(await confirmRemoval(modal, describeMandate(initialValues), hasChanges))) return;
        // Copy: the constant is module-level and this value lands in component state.
        await persistMandate({ ...EMPTY_MANDATE }, true);
    };

    const handleSortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
        if (value.length > 6) {
            value = value.substring(0, 6);
        }
        form.setFieldsValue({ sortCode: value });
    };

    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
        if (value.length > 8) {
            value = value.substring(0, 8);
        }
        form.setFieldsValue({ accountNumber: value });
    };

    if (!hasPermission) {
        return null; // Don't render if user doesn't have permission
    }

    // maskDetails hard-locks the form so a masked view can never be editable,
    // independent of canEdit (the two props can't contradict each other). When the
    // member opts out (skipped), grey the fields out too.
    const isFormDisabled = isLoading || loadFailed || !canEdit || maskDetails || skipped ||
        (selfServiceMode && !canEditDD);

    // Cancellation-terms notice + agreement. The mandate is "being set up" when the
    // fields are editable and no complete mandate is on file yet (creating, or an
    // existing blank / placeholder mandate). While setting up we show the notice with
    // a required tick box; once a mandate is on file we show the same text read-only.
    const noticeText = (cancellationNotice ?? '').trim();
    const noticeConfigured = noticeText.length > 0 && !skipped;
    // "Setting up" = the fields are editable, no complete mandate is on file yet,
    // and a mandate is actually being provided — either it's required (self-service
    // onboarding / editing a blank mandate) or the member has started typing one.
    // This keeps the agreement box out of the admin create flow until a mandate is
    // genuinely being entered (there it's optional).
    const settingUpMandate = !isFormDisabled && !mandateComplete && (mandateRequired || hasMandateInput);
    // While setting up, show the terms with a required tick box; once a complete
    // mandate is on file, show the same terms read-only (already agreed at setup).
    const requireAgreement = noticeConfigured && settingUpMandate;
    const showReadOnlyNotice = noticeConfigured && mandateComplete;
    // Is there a mandate to remove? Every removal affordance — the isRemoval branch in
    // handleSubmit, the Save button's relabel, and the standalone button — derives from
    // this, so they can't disagree about what a blank form means.
    const mandateOnFile = !!initialValues && anyMandateFieldFilled(initialValues);
    // Removal is staff-only and needs something on file to remove.
    const canRemoveMandate = !isCreating && !selfServiceMode && mandateOnFile &&
        loadedFor === membershipId;
    // Staff have cleared every field against a mandate that IS on file — the pending
    // save is a removal, so say so on the button rather than call it "Save Changes".
    const removalPending = canRemoveMandate && hasChanges && !hasMandateInput;
    // Offer the standalone Remove Details button whenever there is a mandate to remove
    // — staff shouldn't have to discover that emptying every field by hand is what
    // takes one off. Two exclusions:
    //  - removalPending: the Save button has already relabelled itself to Remove
    //    Details, and two identical buttons side by side would just be ambiguous.
    //  - isLoading: initialValues still holds the PREVIOUS member's mandate until the
    //    new fetch lands (the panel isn't remounted when the profile changes under it)
    //    while membershipId is already the new member — one click in that window would
    //    blank the wrong record.
    const showRemoveButton = canRemoveMandate && !isLoading && !removalPending;

    const handleAgreedChange = (checked: boolean) => {
        setAgreed(checked);
        onAgreedChange?.(checked);
    };

    return (
        <Card
            title="Direct Debit Information"
            className="mb-4"
            style={{
                backgroundColor,
                borderColor
            }}
        >
            {/* Renders nothing; required for the hook-based removal confirm above. */}
            {modalContextHolder}
            {allowSkip && (
                <>
                    <Alert
                        message="Monthly memberships are billed by direct debit — please complete the section below. If you'd prefer not to set it up now, tick the box and your membership will be reviewed by the club."
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                    />
                    <Checkbox
                        checked={skipped}
                        onChange={(e) => onSkipChange?.(e.target.checked)}
                        style={{ marginBottom: 16 }}
                        data-testid="dd-skip-checkbox"
                    >
                        I'd prefer not to set up direct debit now — the club will review my membership.
                    </Checkbox>
                </>
            )}
            {loadFailed && (
                <Alert
                    message="Couldn't load the direct debit details. The form is locked so nothing is overwritten — reload the page to try again."
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    data-testid="dd-load-error"
                />
            )}
            {maskDetails && (
                <Alert
                    message="Direct debit details are masked. Contact an administrator to view or change full bank details."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            {requireAgreement && (
                <>
                    <Alert
                        message={noticeText}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        data-testid="dd-cancellation-notice"
                    />
                    <Checkbox
                        checked={agreed}
                        onChange={(e) => handleAgreedChange(e.target.checked)}
                        style={{ marginBottom: 16 }}
                        data-testid="dd-cancellation-agree-checkbox"
                    >
                        I agree to the cancellation terms above.
                    </Checkbox>
                </>
            )}
            {showReadOnlyNotice && (
                <Alert
                    message={noticeText}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    data-testid="dd-cancellation-notice-readonly"
                />
            )}
            {selfServiceMode && !canEditDD && (
                <Alert
                    message="Direct debit details are already on file and cannot be changed here. Please contact the club to update."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            <Form<FormValues>
                form={form}
                name={formName}
                layout="vertical"
                onValuesChange={handleValuesChange}
                disabled={isFormDisabled}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="accountHolderName"
                            label="Account Holder Name"
                            rules={mandateRequired ? [{ required: true, message: 'Please enter account holder name' }] : []}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="bankName"
                            label="Bank Name"
                            rules={mandateRequired ? [{ required: true, message: 'Please enter bank name' }] : []}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="sortCode"
                            label="Sort Code (6 digits)"
                            rules={[
                                ...(mandateRequired ? [{ required: true, message: 'Please enter sort code' }] : []),
                                { pattern: /^\d{6}$/, message: 'Sort code must be 6 digits' }
                            ]}
                        >
                            <Input
                                onChange={handleSortCodeChange}
                                placeholder="123456"
                                maxLength={6}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="accountNumber"
                            label="Account Number (8 digits)"
                            rules={[
                                ...(mandateRequired ? [{ required: true, message: 'Please enter account number' }] : []),
                                { pattern: /^\d{8}$/, message: 'Account number must be 8 digits' }
                            ]}
                        >
                            <Input
                                onChange={handleAccountNumberChange}
                                placeholder="12345678"
                                maxLength={8}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                {/* The internal Save button is hidden during create (isCreating) — the
                    mandate is submitted with the member-create request, not saved here. */}
                {canEdit && !isCreating && !maskDetails && !(selfServiceMode && !canEditDD) && (
                    <Row>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            {showRemoveButton && (
                                <Button
                                    danger
                                    onClick={handleRemove}
                                    // An explicit `disabled` overrides the Form's
                                    // disabled context, so fold that state back in.
                                    disabled={isSaving || isFormDisabled}
                                    loading={savingAction === 'remove'}
                                    style={{ marginRight: 8 }}
                                    data-testid="dd-remove-btn"
                                >
                                    Remove Details
                                </Button>
                            )}
                            <Button
                                type="primary"
                                danger={removalPending}
                                onClick={handleSubmit}
                                disabled={!hasChanges || isSaving}
                                loading={savingAction === 'save'}
                                data-testid="dd-save-btn"
                            >
                                {removalPending ? 'Remove Details' : 'Save Changes'}
                            </Button>
                        </Col>
                    </Row>
                )}
            </Form>
        </Card>
    );
};

export default DirectDebitInput;
